import { useEffect, useState } from "react";
import {
  Box, Typography, Card, IconButton, InputBase, Button
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import BuildIcon from "@mui/icons-material/Build";
import BrushIcon from "@mui/icons-material/Brush";
import HomeIcon from "@mui/icons-material/Home";
import DevicesIcon from "@mui/icons-material/Devices";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MovieIcon from "@mui/icons-material/Movie";
import GavelIcon from "@mui/icons-material/Gavel";
import CategoryIcon from "@mui/icons-material/Category";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SchoolIcon from "@mui/icons-material/School";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PetsIcon from "@mui/icons-material/Pets";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useNavigate } from "react-router-dom";

import { Loading } from "../../components/common/Loading";
import SectionService from "../../components/services/SectionServices";
import HomeMap from "../../components/home/HomeMap";
import { getLatestServices, getLatestPages, getMapServices } from "../../actions/profile";
import { getTopCategories } from "../../actions/categories";
import { toggleFavorite } from "../../actions/users";
import { urlImg, isEmpty } from "../../components/common/helpers";
import { conQuienImparte, fotoDe } from "../../components/common/imparte";
import { useIrAEntrar } from "../../utils/destinoAlEntrar";

import { useSelector, useDispatch } from "react-redux";

// Ícono según el nombre de la categoría (con respaldo genérico)
const iconFor = (name = "") => {
  const n = name.toLowerCase();
  if (/repar|manten|plome|electric|construc|albañil|carpint/.test(n)) return <BuildIcon />;
  if (/belle|estétic|estetic|peluqu|barber|maquill|uñas|spa/.test(n)) return <BrushIcon />;
  if (/hogar|casa|limpiez|jardin|domést|domest|mudanz/.test(n)) return <HomeIcon />;
  if (/tecno|comput|software|program|digital|inform|web|sistema/.test(n)) return <DevicesIcon />;
  if (/finanz|contab|fiscal|conta|seguro|inversi|crédit|credit/.test(n)) return <MonetizationOnIcon />;
  if (/entreten|evento|música|musica|fiesta|show|artíst|artist/.test(n)) return <MovieIcon />;
  if (/legal|jurídic|juridic|abogad|notar/.test(n)) return <GavelIcon />;
  if (/salud|médic|medic|enferm|psico|nutri|dental|dentista/.test(n)) return <LocalHospitalIcon />;
  if (/educa|clase|tutor|enseñ|curso|escuel|idioma/.test(n)) return <SchoolIcon />;
  if (/transport|automot|auto|veh|taxi|chofer|carga/.test(n)) return <DirectionsCarIcon />;
  if (/mascot|veterin|animal|perro|gato/.test(n)) return <PetsIcon />;
  if (/comida|gastro|restaur|cocina|chef|cater|banquet/.test(n)) return <RestaurantIcon />;
  if (/deport|fitness|gimnas|entrenam|yoga/.test(n)) return <FitnessCenterIcon />;
  if (/foto|video|cámar|camar|filmac/.test(n)) return <CameraAltIcon />;
  return <CategoryIcon />;
};

// Alterna los dos colores del bloque de categorías
const colorFor = (index) => index % 2 === 0 ? "primary.main" : "success.main";

// Flechas del carrusel de categorías. Swiper les agrega `swiper-button-disabled` al llegar
// a un extremo y `swiper-button-lock` cuando todas las categorías caben (no hacen falta).
const navCatSx = {
  width: 32,
  height: 32,
  bgcolor: "white",
  boxShadow: 1,
  "&:hover": { bgcolor: (t) => alpha(t.palette.secondary.main, 0.05) },
  "&.swiper-button-disabled": { opacity: 0.35, cursor: "default" },
  "&.swiper-button-lock": { display: "none" },
};

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.user);
  const loading = useSelector(state => state.user.loading);
  const irAEntrar = useIrAEntrar();

  // Sin sesión no hay nombre: el saludo queda solo con la invitación
  const nombre = user?.name || "";
  const [busqueda, setBusqueda] = useState("");
  const [latestServices, setLatestServices] = useState([]);
  const [latestPages, setLatestPages] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [mapServices, setMapServices] = useState([]);
  // Flechas del carrusel de categorías: Swiper las recibe cuando ya existen en el DOM.
  const [catPrev, setCatPrev] = useState(null);
  const [catNext, setCatNext] = useState(null);

  useEffect(() => {
    const fetchTopCategories = async () => {
      const data = await getTopCategories(10);
      setTopCategories(data || []);
    };
    fetchTopCategories();
  }, []);

  useEffect(() => {
    const fetchMapServices = async () => {
      const data = await getMapServices(100);
      // Resolvemos la URL de la foto del proveedor (avatar social http/// o de Cloudinary)
      // para pintarla en el pin del mapa.
      // (si el servicio lo imparte otra persona, el pin lleva su foto y su nombre)
      const withAvatar = (data || []).map(conQuienImparte).map(s => ({
        ...s,
        authorAvatarUrl: fotoDe({ _id: s.autorId, avatar: s.authorAvatar, versionAvatar: s.authorAvatarVersion })
      }));
      setMapServices(withAvatar);
    };
    fetchMapServices();
  }, []);

  useEffect(() => {
    const fetchLatestPages = async () => {
      const data = await getLatestPages();
      const formattedPages = data.map(page => {
        let finalAvatarUrl = "";
        if (!isEmpty(page.authorAvatar)) {
          const avatarStr = page.authorAvatar.toString();
          if (avatarStr.startsWith("http")) {
            finalAvatarUrl = avatarStr;
          } else {
            const version = page.authorAvatarVersion ? `v${page.authorAvatarVersion}/` : "";
            finalAvatarUrl = `${urlImg}/w_150,h_150,c_fill/${version}users/${page.userId}/${page.authorAvatar}`;
          }
        }

        return {
          ...page,
          authorAvatar: finalAvatarUrl,
          image: !isEmpty(page.image) ? `${urlImg}/w_400,h_250,c_fill/paginas/${page._id}/${page.image}` : ""
        };
      });
      setLatestPages(formattedPages);
    };
    fetchLatestPages();
  }, []);

  useEffect(() => {
    const fetchLatestServices = async () => {
      const data = await getLatestServices();
      // La tarjeta muestra a quien imparte el servicio (por omisión, quien lo creó)
      const formattedServices = data.map(conQuienImparte).map(serv => ({
        ...serv,
        authorAvatar: fotoDe({ _id: serv.autorId, avatar: serv.authorAvatar, versionAvatar: serv.authorAvatarVersion }, 'w_150,h_150,c_fill'),
        image: !isEmpty(serv.image) ? `${urlImg}/w_400,h_250,c_fill/servicios/${serv._id}/${serv.image}` : ""
      }));
      setLatestServices(formattedServices);
    };
    fetchLatestServices();
  }, []);

  const servicesWithFav = latestServices.map(s => ({
    ...s,
    favorite: user?.favorites?.some(f => f.toString() === s._id?.toString())
  }));

  // Grupo/negocio o independiente: el tipo de la página (un servidor sin actualizar no lo manda: más de un miembro = grupo)
  const esGrupo = p => (p.tipo ? p.tipo === 'grupo' : p.membersCount > 1);

  const pagesWithFav = latestPages.map(p => ({
    ...p,
    favorite: user?.favorites?.some(f => f.toString() === p._id?.toString())
  }));

  if (loading) {
    return <Loading />
  }

  // Sin sesión el corazón manda a entrar (antes la petición fallaba sin decir nada); al entrar regresa al inicio
  const changeFavoriteService = (service) => {
    if (!user?._id) return irAEntrar();
    dispatch(toggleFavorite(service._id));
  }

  const irA = where => {
    navigate(where);
  }

  // Mismo destino que el buscador de arriba
  const buscarServicios = (e) => {
    e.preventDefault();
    const q = busqueda.trim();
    navigate(q ? `/buscar?q=${encodeURIComponent(q)}` : "/buscar");
  };

  return (
    <Box className="main-page">
      {/* Saludo personalizado (sobre fondo blanco, como pidió dirección) */}
      <Typography variant="h6" sx={{ mb: 2, whiteSpace: { xs: "normal", md: "nowrap" }, textWrap: "balance", color: "primary.main", fontWeight: 700 }}>
        {nombre && (
          <>
            <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>Hola, </Box>
            {nombre} —{" "}
          </>
        )}
        Encuentra un servicio cerca de ti
      </Typography>

      {/* Buscador del inicio solo en pantallas angostas: ahí el de arriba es una lupa; en escritorio ya se ve completo arriba */}
      <Box
        component="form"
        role="search"
        onSubmit={buscarServicios}
        sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1, mb: 2, pl: 1.5, pr: 0.75, py: 0.75, borderRadius: 2, bgcolor: "sutil" }}
      >
        <SearchIcon color="primary" fontSize="small" />
        <InputBase
          id="buscar-inicio"
          name="q"
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="¿Qué servicio buscas?"
          inputProps={{ "aria-label": "Buscar servicios", enterKeyHint: "search" }}
          sx={{ flex: 1, minWidth: 0, fontSize: 15 }}
        />
        <Button type="submit" variant="contained" color="primary" disableElevation sx={{ flexShrink: 0, textTransform: "none", borderRadius: 2 }}>
          Buscar
        </Button>
      </Box>

      {/* Mapa al centro */}
      <HomeMap servicios={mapServices} />

      {topCategories.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
            <Typography color="primary" variant="h6" fontWeight="bold">
              Explora nuestras categorías
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <IconButton ref={setCatPrev} aria-label="Categorías anteriores" sx={navCatSx}>
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton ref={setCatNext} aria-label="Más categorías" sx={navCatSx}>
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          <Swiper
            modules={[Navigation]}
            navigation={{ prevEl: catPrev, nextEl: catNext }}
            spaceBetween={12}
            slidesPerView={2}
            grabCursor
            breakpoints={{
              640: { slidesPerView: 3 },
              900: { slidesPerView: 4 },
              1200: { slidesPerView: 5 },
            }}
          >
            {topCategories.map((cat, index) => (
              <SwiperSlide key={cat._id || index}>
                <Card
                  onClick={() => irA(`/buscar?categoria=${cat._id}`)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.75,
                    py: 1.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    boxShadow: "none",
                    border: 1, borderColor: 'sutil',
                    transition: "border-color .15s, background-color .15s",
                    "&:hover": { borderColor: "secondary.main", bgcolor: (t) => alpha(t.palette.secondary.main, 0.05) },
                    "& svg": { fontSize: 22 },
                  }}
                >
                  <Box sx={{ color: colorFor(index), display: "flex", flexShrink: 0 }}>
                    {iconFor(cat.name)}
                  </Box>
                  <Typography variant="body2" color="primary" fontWeight="bold" noWrap sx={{ minWidth: 0 }}>
                    {cat.name}
                  </Typography>
                </Card>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      )}
      <SectionService
        title="Servicios destacados"
        subtitle="Algunos servicios que creemos podrían interesarte"
        services={servicesWithFav}
        favoriteService={changeFavoriteService}
        irA={() => irA("/admin/servicios")}
        onVerDetalle={(srv) => irA(`/servicio/${srv.userId}/${srv._id}`)}
        showPriece
      />
      <SectionService
        title="Páginas de servicios independientes"
        subtitle="Encuentra al profesional ideal para lo que necesites"
        services={pagesWithFav.filter(p => !esGrupo(p))}
        favoriteService={changeFavoriteService}
        irA={() => irA("/")}
        onVerDetalle={(page) => irA(`/pagina/${page._id}`)}
        showAvailability
      />
      <SectionService
        title="Páginas de grupos y negocios"
        subtitle="Empresas y equipos especializados a tu disposición"
        services={pagesWithFav.filter(esGrupo)}
        favoriteService={changeFavoriteService}
        irA={() => irA("/")}
        onVerDetalle={(page) => irA(`/pagina/${page._id}`)}
        showAvailability
      />
    </Box>
  );
}

export default Home;
